import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

/**
 * Client-side SVD mechanism, mirroring `should-use-design-system.ts`. `shouldUseSvdCard()` reflects
 * `pageProps.isSvdEnabled` — a single, sitewide, per-USER decision from the `ramp_svd-rollout-web`
 * bucket (`isSvdRolloutBucketActive`). There is no per-page/per-surface gate: the entire app is
 * either SVD or not for a given user.
 *
 * This function only REFLECTS the page prop — it does not own the kill-switch. The decision is
 * server-side (`resolveIsSvdEnabled`): browser traffic evaluates the experiment behind
 * `ENABLE_SVD_FLOW_DISCOVERY`, native-app web-views follow the `is_svd_enabled_on_app` gate alone
 * (see `svd.config` / `svd-flags`) — and the server ships the result. `_app` seeds the module state below from that prop;
 * `_document` reflects it onto `<html>` so the browser reads the attribute (hydration-safe — never
 * this module state). When the flow is dark the server always sends the flag `false`, so every
 * reader here returns false. Module state is safe because the pages-router render pass is
 * synchronous.
 *
 * This constant lives HERE (not `svd.config`) on purpose: this module is on the client render path
 * (every product-card section reads `shouldUseSvdCard()`), so it must stay free of the statsig
 * experiment registry.
 */
export const DOCUMENT_HTML_DATA_SVD_CARD_ATTR = 'data-svd-card'
export const DOCUMENT_HTML_DATA_SVD_CARD_VALUE = 'enabled'

let svdCardEnabledForRender: boolean | undefined

export function setSvdCardEnabledInMemory(enabled: boolean | undefined | null): void {
  if (isBrowser()) {
    // No DOM mutation during render — the `<html>` attribute is only synced at commit time
    // by syncSvdCardHtmlAttribute() (see should-use-design-system.ts for the rationale).
    // `undefined` = reset to the attribute fallback (tests + the pre-`_app` window); `_app`
    // pins no-decision pages to `false` — the decision is per-page.
    svdCardEnabledForRender = enabled === undefined ? undefined : Boolean(enabled)
    return
  }

  // Server: always reset — fail-closed, never leak the previous request's value.
  svdCardEnabledForRender = Boolean(enabled)
}

export function shouldUseSvdCard(): boolean {
  if (isBrowser()) {
    if (svdCardEnabledForRender !== undefined) {
      return svdCardEnabledForRender
    }
    return document.documentElement.getAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR) === DOCUMENT_HTML_DATA_SVD_CARD_VALUE
  }

  return svdCardEnabledForRender ?? false
}

/**
 * Sole client-side writer of the `data-svd-card` `<html>` attribute. Called by `_app` from a layout
 * effect (post-commit, pre-paint) so the attribute never flips under a still-visible outgoing page.
 * A no-op while the decision is undecided (`undefined`) — mirrors `syncDesignSystemHtmlAttributes`.
 */
export function syncSvdHtmlAttributes(): void {
  if (!isBrowser()) {
    return
  }

  if (svdCardEnabledForRender !== undefined) {
    if (svdCardEnabledForRender) {
      document.documentElement.setAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR, DOCUMENT_HTML_DATA_SVD_CARD_VALUE)
    } else {
      document.documentElement.removeAttribute(DOCUMENT_HTML_DATA_SVD_CARD_ATTR)
    }
  }
}

/** Sole reader used by `_document` to stamp `data-svd-card` on the initial SSR pass. */
export function isSvdCardEnabledFromNextData(nextData: {
  props?: { pageProps?: { isSvdEnabled?: boolean } }
}): boolean {
  return Boolean(nextData.props?.pageProps?.isSvdEnabled)
}
